import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { VaultChipRow } from "../components/VaultChipRow";
import { SwipeRow } from "../components/SwipeRow";
import { CaptureDrawer } from "../components/CaptureDrawer";
import { TabBar } from "../components/TabBar";
import { useVault } from "../contexts/VaultContext";
import type { CaptureSummary } from "../lib/api-types";
import type { Vault } from "../contexts/vault-types";

const VAULTS: Vault[] = ["main", "health", "finance", "work", "family"];

export default function Inbox() {
  const { selected, toggle } = useVault();
  const navigate = useNavigate();
  const [items, setItems] = useState<CaptureSummary[]>([]);
  const [drawer, setDrawer] = useState<{ vault: Vault; slug: string } | null>(null);
  const [showMove, setShowMove] = useState<{ vault: Vault; slug: string } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const currentVault = selected[0] ?? "main";

  const fetchItems = useCallback(async () => {
    const res = await fetch(`/api/captures?vault=${currentVault}&limit=30`);
    if (res.ok) setItems(await res.json());
  }, [currentVault]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const remove = async (slug: string) => {
    if (!confirm("Delete this capture?")) return;
    setActionError(null);
    try {
      const res = await fetch(`/api/captures/${currentVault}/${slug}`, { method: "DELETE" });
      if (!res.ok) {
        setActionError(`Delete failed (${res.status}) — capture not removed`);
        return;
      }
      setItems(prev => prev.filter(x => x.id !== slug));
    } catch {
      setActionError("Network error — capture not removed");
    }
  };

  const move = async (slug: string, toVault: Vault) => {
    setActionError(null);
    try {
      const res = await fetch(`/api/captures/${currentVault}/${slug}/move`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ toVault }),
      });
      if (!res.ok) {
        setActionError(`Move failed (${res.status})`);
        return;
      }
      setItems(prev => prev.filter(x => x.id !== slug));
      setShowMove(null);
    } catch {
      setActionError("Network error — capture not moved");
    }
  };

  return (
    <div className="min-h-screen pb-20 px-4 pt-3">
      <h1 className="text-attic-accent font-bold text-base mb-3">attic</h1>
      <h2 className="text-lg font-bold mb-2">Inbox</h2>
      <VaultChipRow selected={selected} onToggle={toggle} />
      {items.length === 0 && (
        <p className="text-attic-dim text-xs text-center mt-8">No captures yet in this vault.</p>
      )}
      {items.map(item => (
        <SwipeRow
          key={item.id}
          actions={[
            {
              label: "Modify",
              icon: "✎",
              color: "bg-emerald-600",
              onClick: () => navigate(`/modify/${currentVault}/${item.id}`),
            },
            {
              label: "Move",
              icon: "↳",
              color: "bg-attic-accent",
              onClick: () => setShowMove({ vault: currentVault, slug: item.id }),
            },
            {
              label: "Delete",
              icon: "🗑",
              color: "bg-red-600",
              onClick: () => remove(item.id),
            },
          ]}
        >
          <div className="flex">
            <button
              onClick={() => setDrawer({ vault: currentVault, slug: item.id })}
              className="flex-1 text-left p-3"
            >
              <div className="flex justify-between items-baseline mb-1">
                <h4 className="text-sm font-semibold">{item.title}</h4>
                <span className="text-[10px] text-attic-dim">{item.capturedAt.slice(0, 10)}</span>
              </div>
              <p className="text-[10px] uppercase tracking-wider text-attic-accent font-semibold mb-1">{item.source}</p>
              <p className="text-xs text-attic-dim leading-relaxed">{item.summary}</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {item.tags.map(t => (
                  <span key={t} className="text-[9px] bg-attic-accent-bg text-attic-accent rounded px-1.5 py-0.5">{t}</span>
                ))}
              </div>
            </button>
            {item.url && (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-attic-accent text-lg font-semibold px-3 flex items-center"
                aria-label="View original"
              >
                ↗
              </a>
            )}
          </div>
        </SwipeRow>
      ))}
      {drawer && <CaptureDrawer vault={drawer.vault} slug={drawer.slug} onClose={() => setDrawer(null)} />}
      {actionError && (
        <div className="fixed bottom-20 left-4 right-4 bg-red-600 text-white text-xs rounded-lg px-3 py-2 shadow-lg z-50 flex justify-between items-center">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="ml-2 font-bold">×</button>
        </div>
      )}
      {showMove && (
        <div
          className="fixed top-0 left-0 right-0 bottom-14 bg-black/40 flex items-end z-[60]"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowMove(null)}
        >
          <div className="w-full bg-attic-surface rounded-t-2xl p-4" onClick={(e) => e.stopPropagation()}>
            <div className="w-8 h-1 bg-attic-border rounded mx-auto mb-3" />
            <h3 className="text-center font-semibold mb-3 text-sm">Move to vault</h3>
            {VAULTS.filter(v => v !== currentVault).map(v => (
              <button
                key={v}
                onClick={(e) => { e.stopPropagation(); move(showMove.slug, v); }}
                className="w-full text-left p-3 border-b border-attic-border last:border-b-0 text-sm capitalize active:bg-attic-bg"
              >
                {v}
              </button>
            ))}
            <button
              onClick={() => setShowMove(null)}
              className="w-full text-center p-3 mt-2 text-attic-accent text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <TabBar />
    </div>
  );
}
